import { ProductInfoType } from 'src/types/product';
import { RenderingSDK, ProductResponseType, ProductVersionsResponseType } from '@rendering/web-client';

const mapValues = (values?: Array<{ type: string; value: string }> | null): Array<{ type: string; value: string }> =>
    (values ?? []).map((entry) => ({
        type: entry.type,
        value: entry.value,
    }));
function parseProductData(result: ProductResponseType) {
    if (!result) {
        return {};
    }

    let modified;
    // fallback to show created date when modified is not defined as it might be first iteration of the product
    if ('modified' in result && result.modified) {
        modified = new Date(result.modified);
    } else if ('auditInfo' in result && result.auditInfo?.modifiedAt) {
        modified = new Date(result.auditInfo.modifiedAt);
    } else if ('createdAt' in result && result.createdAt) {
        modified = new Date(result.createdAt);
    }

    let options = {};
    if ('options' in result && result.options) {
        options = result.options.map((option) => ({
            values: mapValues(option.values),
            name: option.name,
            type: option.type,
        }));
    }

    return {
        name: result.name,
        modified,
        description: result.description,
        options,
    };
}

export function retrieveProductData(sdk: RenderingSDK, { sku, skuVersion }: Omit<ProductInfoType, 'attributes'>) {
    if (skuVersion) {
        return sdk.Products.getByVersion(sku, skuVersion, true).then(parseProductData);
    }

    return sdk.Products.getMCPFromVIP(sku).then(parseProductData);
}

function mapProductVersion(res: ProductVersionsResponseType) {
    const items = res._embedded.item;
    if (!items || !Array.isArray(items)) {
        return [];
    }
    return items
        .sort((a, b) => (b.version || 0) - (a.version || 0))
        .map((proVer) => ({
            productId: proVer.productId,
            version: proVer.version,
            status: proVer.status.toLowerCase(),
            current: 'current' in proVer ? proVer.current : undefined,
            name: 'name' in proVer ? proVer.name : undefined,
        }));
}

export async function getPRDProductVersions(sdk: RenderingSDK, sku: string) {
    return sdk.Products.getPRDProductVersions(sku, true).then((res) => mapProductVersion(res));
}

export function getProductVersions(sdk: RenderingSDK, sku: string) {
    return sdk.Products.getVersions(sku).then((res) => mapProductVersion(res));
}

/**
 * Retrieves mcpSku and/or version of the mcpSku related to the vistaprint platform sku + version.
 * @param options
 * @param options.sku - The platformID.
 * @param options.version - The platformID version.
 * @returns - The MCP Sku and version related to the platformId .
 */
export function retrievePlatformId(sdk: RenderingSDK, { sku, skuVersion, attributes }: ProductInfoType) {
    return sdk.Products.getMCPFromPRD(sku, skuVersion?.toString(), attributes, true).then((res) => ({
        merchantSku: res.productId,
        merchantVersion: res.version,
        merchantVariables: res.attributes,
    }));
}
